import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, ShieldX } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { user, loading, isAdmin, adminCheckStatus, adminCheckError, retryAdminCheck } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  // Still checking admin status
  if (requireAdmin && adminCheckStatus === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  // Error verifying admin status - show retry option
  if (requireAdmin && adminCheckStatus === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Erro de Verificação</h1>
          <p className="text-muted-foreground mb-4">
            {adminCheckError || 'Não foi possível verificar suas permissões de administrador.'}
          </p>
          <div className="space-y-2">
            <Button onClick={retryAdminCheck} className="w-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar Novamente
            </Button>
            <p className="text-xs text-muted-foreground">
              Se o problema persistir, verifique sua conexão com a internet ou entre em contato com o suporte.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // User verified but not admin
  if (requireAdmin && adminCheckStatus === 'verified' && !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <ShieldX className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Acesso Negado</h1>
          <p className="text-muted-foreground mb-4">
            Você não tem permissão de administrador para acessar esta página.
          </p>
          <p className="text-xs text-muted-foreground">
            Email: {user.email}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
